import { useEffect, useRef, useState } from 'react';
import { FormInputProps } from '@/@types/form.types';
import EListPopupPickerWithSearchV2 from '@/components/everridge/EListPopupPickerWithSearchV2';
import { useApartmentsLookupApi } from '@/features/apartments/hooks/useApartmentsLookupApi';

type Props<T extends {}> = {
    selectVariant?: 'single' | 'multi';
    showSearch?: boolean;
    icon?: React.FC<React.SVGProps<SVGSVGElement>>;
    hotelId?: string;
    addResetButton: boolean;
} & FormInputProps<T>;

const ApartmentsLookup = <T extends {}>(props: Props<T>) => {
    const [searchValue, setSearchValue] = useState('');
    const { placeholder = 'Select apartment', label = 'Apartments', hotelId } = props;

    const loaderRef = useRef<HTMLDivElement>(null);

    const {
        apartmentsLookupList,
        fetchApartmentsLookupListNextPage,
        hasApartmentsLookupListNextPage,
        isFetchingApartmentsLookupListNextPage,
    } = useApartmentsLookupApi({ search: searchValue, hotelId });

    useEffect(() => {
        if (
            !loaderRef.current ||
            !hasApartmentsLookupListNextPage ||
            isFetchingApartmentsLookupListNextPage
        )
            return;

        const scrollableContainer = loaderRef.current.closest('.overflow-y-scroll');

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    fetchApartmentsLookupListNextPage();
                }
            },
            {
                root: scrollableContainer,
                rootMargin: '100px',
            },
        );

        const current = loaderRef.current;
        observer.observe(current);

        return () => observer.unobserve(current);
    }, [
        fetchApartmentsLookupListNextPage,
        hasApartmentsLookupListNextPage,
        isFetchingApartmentsLookupListNextPage,
    ]);

    return (
        <EListPopupPickerWithSearchV2
            {...props}
            label={label}
            placeholder={placeholder}
            list={apartmentsLookupList ?? []}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            ref={loaderRef}
        />
    );
};

export default ApartmentsLookup;
